import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Select from "react-select";

const buildStyles = (stylesOverride = {}) => {
  const themeColor = "#4F46E5"; // Matches your app's indigo accent
  const base = {
   option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? themeColor 
        : state.isFocused 
        ? "#F5F7FF" // Soft indigo wash on hover
        : "#FFFFFF",
      color: state.isSelected ? "#FFFFFF" : "#475569",
      padding: "10px 14px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      "&:active": {
        backgroundColor: themeColor,
        color: "#FFFFFF",
      },
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "transparent", // Let the parent container's color show
      border: "none",                 // Remove internal border
      boxShadow: "none",              // Remove internal shadow
      minHeight: "42px",
      cursor: "pointer",
      "&:hover": { border: "none" },
    }),
     valueContainer: (provided) => ({
      ...provided,
      padding: "2px 12px", // Matches your text input padding
    }),
    indicatorSeparator: () => ({
      display: "none", // Cleaner, modern look
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? themeColor : "#94A3B8",
      "&:hover": { color: themeColor },
    }),
    
    singleValue: (provided) => ({
      ...provided,
      color: "#1E293B", // Deep slate for readability
      fontSize: "14px",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#94A3B8",
      fontSize: "14px",
      fontWeight: "400",
    }),
    container: (provided) => ({ ...provided, padding: "0px", border: "0px" }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
      borderRadius: "12px", // Matches your card and dropdown radius
      border: "1px solid #E2E8F0",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      padding: "4px",
      marginTop: "8px",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
   menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      padding: "0",
    }),
    input: (provided) => ({
      ...provided,
      color: "#1E293B",
      fontSize: "14px",
    }),
  };

  return { ...base, ...stylesOverride };
};

// helpers
const isOptionObject = (v) =>
  v && typeof v === "object" && Object.prototype.hasOwnProperty.call(v, "value");

const SelectSearch = ({
  options = [],
  className = "",
  onChange = () => {},
  placeholder = "Select...",
  value = null, // can be option object OR raw value (string/number)
  onType = () => {},
  stylesOverride = null,

  menuPortalTarget = typeof document !== "undefined" ? document.body : null,
  menuPosition = "fixed",
  isClearable = true,
  isSearchable = true,
  isDisabled = false,
  name,
}) => {
  const styles = useMemo(() => buildStyles(stylesOverride || {}), [stylesOverride]);

  // ✅ make sure react-select gets the full option object
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;

    // if caller passed the option object already, use it
    if (isOptionObject(value)) return value;

    // otherwise, treat it as raw value and resolve from options
    const v = String(value);
    return options.find((o) => String(o?.value) === v) || null;
  }, [value, options]);

  // ✅ normalize onChange: return BOTH the option and raw value to caller
  const handleChange = (opt) => {
    // opt is either an option object or null
    onChange(opt);
  };

  return (
    <Select
      name={name}
      options={options}
      className={className}
      onChange={handleChange}
      placeholder={placeholder}
      value={selectedOption}
      styles={styles}
      onKeyDown={onType}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
    />
  );
};

SelectSearch.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,

  // can be {value,label} OR raw "92"
  value: PropTypes.any,

  onType: PropTypes.func,
  stylesOverride: PropTypes.object,

  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  name: PropTypes.string,
};

export default SelectSearch;
